/*
 * The targets the coached way sets from an expenditure — the nutrition spec, The goal itself.
 * The expenditure itself is worked out in `expenditure.ts`, to the numbers in
 * `/specs/features/expenditure.spec.md`, and everything of it a client reads is re-exported
 * from here so the web app has one import to make.
 *
 * Every app works this out for itself from what it already holds, so the nutrition spec pins
 * every number here, and the Android port in `data/Expenditure.kt` runs the same test cases.
 */

export * from './expenditure'

/** Calories in a kilogram of body weight, as every calculator on earth rounds it. */
export const KCAL_PER_KG = 7700

/**
 * How a goal is coached, once the aim is settled. These are the program settings: the shape
 * of the plate, how low the calories are let to go, how much protein is wanted, and which
 * days of the week carry more of the week's calories than others. They are what turns one
 * expenditure and one rate into seven days' targets.
 */
export const DIETS = ['balanced', 'low-fat', 'low-carb', 'keto'] as const
export type Diet = (typeof DIETS)[number]

/** The share of a day's calories that comes from fat, per diet. */
const DIET_FAT_SHARE: Record<Diet, number> = {
  balanced: 0.28,
  'low-fat': 0.2,
  'low-carb': 0.4,
  keto: 0.7,
}

/** The most carbohydrate a keto day carries; whatever the protein leaves over goes to fat. */
const KETO_CARB_CEILING = 30

export const PROTEIN_INTAKES = ['low', 'moderate', 'high', 'extra-high'] as const
export type ProteinIntake = (typeof PROTEIN_INTAKES)[number]

/** Grams of protein for each kilogram of body weight, per preference. */
export const PROTEIN_G_PER_KG: Record<ProteinIntake, number> = {
  low: 1.4,
  moderate: 1.8,
  high: 2.2,
  'extra-high': 2.6,
}

export const CALORIE_FLOORS = ['standard', 'low'] as const
export type CalorieFloor = (typeof CALORIE_FLOORS)[number]

/** The fewest calories a day is coached to, per floor. */
export const FLOOR_CALORIES: Record<CalorieFloor, number> = { standard: 1200, low: 1000 }

export function floorCalories(floor: CalorieFloor): number {
  return FLOOR_CALORIES[floor]
}

export const CALORIE_LEVELS = ['low', 'normal', 'high'] as const
export type CalorieLevel = (typeof CALORIE_LEVELS)[number]

/** What a day of each level carries against a level day, before the week is balanced again. */
const LEVEL_WEIGHT: Record<CalorieLevel, number> = { low: 0.85, normal: 1, high: 1.15 }

/** Seven levels, Monday first, all of them level — the distribution a goal starts from. */
export function evenDistribution(): CalorieLevel[] {
  return ['normal', 'normal', 'normal', 'normal', 'normal', 'normal', 'normal']
}

/** How far either side of the current trend weight a maintaining goal may aim. */
export const MAINTAIN_WINDOW_KG = 3

/** How long a maintaining goal takes to close a gap between the trend and its target. */
const MAINTAIN_DAYS_TO_CLOSE = 56

/** The share of the expenditure a maintaining goal may move by, either way. */
const MAINTAIN_SWING = 0.1

export interface Program {
  diet: Diet
  calorieFloor: CalorieFloor
  proteinIntake: ProteinIntake
  /** Seven levels, Monday first. */
  distribution: readonly CalorieLevel[]
}

export const DEFAULT_PROGRAM: Program = {
  diet: 'balanced',
  calorieFloor: 'standard',
  proteinIntake: 'moderate',
  distribution: evenDistribution(),
}

export interface Targets {
  calories: number
  protein: number
  carbs: number
  fat: number
}

export interface GuidedTargets extends Targets {
  /** The seven days the week is made of, Monday first. */
  days: Targets[]
  /** Where a maintaining goal lets the calories move as it adapts, or null for any other aim. */
  range: { low: number; high: number } | null
}

/**
 * What a maintaining goal eats: the expenditure, nudged towards its target trend weight. A
 * body sitting on its target eats what it spends; one two kilograms above it eats a little
 * less until it is not. The nudge closes the gap over eight weeks and never moves the
 * calories by more than a tenth either way, which is what makes maintenance dynamic rather
 * than a number set once and left.
 */
export function maintenanceCalories(
  expenditure: number,
  trendWeightKg: number | null,
  targetWeightKg: number | null,
): number {
  if (trendWeightKg === null || targetWeightKg === null) {
    return expenditure
  }
  const gap = trendWeightKg - targetWeightKg
  const swing = expenditure * MAINTAIN_SWING
  const nudge = (-gap * KCAL_PER_KG) / MAINTAIN_DAYS_TO_CLOSE
  return expenditure + Math.max(-swing, Math.min(swing, nudge))
}

/** The macros a day's calories are split into, per diet and protein preference. */
function macrosOf(calories: number, protein: number, diet: Diet): Targets {
  const left = Math.max(0, calories - protein * 4)
  if (diet === 'keto') {
    const carbs = Math.min(KETO_CARB_CEILING, left / 4)
    return {
      calories,
      protein,
      carbs: Math.round(carbs),
      fat: Math.round(Math.max(0, left - carbs * 4) / 9),
    }
  }
  const fat = Math.round((calories * DIET_FAT_SHARE[diet]) / 9)
  return {
    calories,
    protein,
    carbs: Math.round(Math.max(0, calories - protein * 4 - fat * 9) / 4),
    fat,
  }
}

/**
 * The targets the guided way arrives at from an expenditure, an aim and a body weight:
 * calories moved by what the rate asks for — a kilogram a week is KCAL_PER_KG spread over
 * seven days — never below the floor the program chose, protein from the body weight and the
 * preference, fat the share of the calories the diet asks for, and whatever calories remain
 * as carbohydrates. The week's calories are then spread over its days by the distribution,
 * which moves calories between days without changing what the week comes to. Worked out here,
 * once, so every app arrives at the same numbers from the same aim.
 */
export function guidedTargets(
  expenditure: number,
  aim: 'lose' | 'maintain' | 'gain',
  rateKgPerWeek: number,
  weightKg: number,
  program: Program = DEFAULT_PROGRAM,
  targetWeightKg: number | null = null,
  trendWeightKg: number | null = null,
): GuidedTargets {
  const floor = FLOOR_CALORIES[program.calorieFloor]
  const daily = (rateKgPerWeek * KCAL_PER_KG) / 7
  const wanted =
    aim === 'maintain'
      ? maintenanceCalories(expenditure, trendWeightKg ?? weightKg, targetWeightKg)
      : expenditure + (aim === 'lose' ? -daily : daily)
  const base = Math.max(floor, wanted)
  const protein = Math.round(weightKg * PROTEIN_G_PER_KG[program.proteinIntake])
  const levels = program.distribution.length === 7 ? [...program.distribution] : evenDistribution()
  const weights = levels.map((level) => LEVEL_WEIGHT[level])
  const total = weights.reduce((sum, each) => sum + each, 0)
  // Balanced back to seven, so moving calories between days never changes what the week eats.
  const days = weights.map((weight) =>
    macrosOf(Math.round(Math.max(floor, (base * weight * 7) / total)), protein, program.diet),
  )
  const mean = (of: (day: Targets) => number) =>
    Math.round(days.reduce((sum, day) => sum + of(day), 0) / days.length)
  return {
    calories: mean((day) => day.calories),
    protein,
    carbs: mean((day) => day.carbs),
    fat: mean((day) => day.fat),
    days,
    range:
      aim === 'maintain'
        ? {
            low: Math.round(Math.max(floor, expenditure * (1 - MAINTAIN_SWING))),
            high: Math.round(expenditure * (1 + MAINTAIN_SWING)),
          }
        : null,
  }
}
